import { Controller } from "@hotwired/stimulus";

// Filter panel controller - manages separated trigger buttons and content sheets
// Controller is on .search-page, triggers are in filter bar, sheets are in sheets container
// Desktop (>=768px): dropdown below trigger with click-outside-to-close
// Mobile (<768px): bottom sheet with backdrop, scroll lock, and map interaction management
export default class extends Controller {
  static targets = ["sheet", "container"];

  connect() {
    this.initialState = null;
    this.activeSheet = null;
    this.activeTrigger = null;
    this.mapElement = document.getElementById("search-map");
    this.mobileSheetElement = document.querySelector(
      "[data-controller*='mobile-sheet']",
    );

    // Bind handlers
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this);
    this.handleResize = this.handleResize.bind(this);

    // Check initial breakpoint
    this.checkBreakpoint();

    // Watch for resize
    window.addEventListener("resize", this.handleResize);
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("click", this.closeOnClickOutside);
  }

  checkBreakpoint() {
    this.isMobile = window.innerWidth < 768;
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.checkBreakpoint();

    // If breakpoint changed while panel was open, close gracefully
    if (wasMobile !== this.isMobile && this.isOpen()) {
      this.close();
    }
  }

  isOpen() {
    return this.activeSheet !== null;
  }

  // Find the sheet element matching the filter type
  findSheet(filterType) {
    return this.sheetTargets.find(
      (sheet) => sheet.dataset.filterType === filterType,
    );
  }

  // Find the trigger button matching the filter type
  findTrigger(filterType) {
    return this.element.querySelector(
      `[data-filter-type="${filterType}"].filter-panel__trigger`,
    );
  }

  toggle(event) {
    event.stopPropagation();

    const filterType = event.currentTarget.dataset.filterType;
    const sheet = this.findSheet(filterType);

    if (!sheet) return;

    // If clicking the same filter that's open, close it
    if (this.activeSheet === sheet) {
      this.close();
    } else {
      // Close any open sheet first, then open the new one
      if (this.isOpen()) {
        this.close();
      }
      this.open(filterType, event.currentTarget);
    }
  }

  open(filterType, trigger = null) {
    const sheet = this.findSheet(filterType);
    if (!sheet) return;

    // Store active references
    this.activeSheet = sheet;
    this.activeTrigger = trigger || this.findTrigger(filterType);

    // Capture initial state
    this.initialState = this.getCheckboxState(sheet);

    // Add open class to sheet
    sheet.classList.add("filter-sheet--open");

    // Call mode-specific open
    if (this.isMobile) {
      this.openMobile();
    } else {
      this.openDesktop();
    }
  }

  close() {
    if (!this.isOpen()) return;

    const sheet = this.activeSheet;

    // Check dirty state before closing
    const dirty = this.isDirty(sheet);

    // Remove open class
    sheet.classList.remove("filter-sheet--open");

    // Call mode-specific close
    if (this.isMobile) {
      this.closeMobile();
    } else {
      this.closeDesktop();
    }

    // Dispatch event if filters changed
    if (dirty) {
      this.element.dispatchEvent(
        new CustomEvent("filter-panel:changed", { bubbles: true }),
      );
    }

    // Clear active references
    this.activeSheet = null;
    this.activeTrigger = null;
    this.initialState = null;
  }

  // Desktop behaviors
  openDesktop() {
    // Position the sheet near the trigger button
    if (this.activeTrigger && this.activeSheet) {
      this.positionDropdown();
    }
    document.addEventListener("click", this.closeOnClickOutside);
  }

  closeDesktop() {
    document.removeEventListener("click", this.closeOnClickOutside);
    // Reset positioning
    if (this.activeSheet) {
      this.activeSheet.style.left = "";
      this.activeSheet.style.top = "";
    }
  }

  positionDropdown() {
    const trigger = this.activeTrigger;
    const sheet = this.activeSheet;

    // Get trigger position relative to viewport
    const triggerRect = trigger.getBoundingClientRect();

    // Position sheet below trigger
    sheet.style.left = `${triggerRect.left}px`;
    sheet.style.top = `${triggerRect.bottom + 4}px`;
  }

  closeOnClickOutside(event) {
    // Don't close if clicking on the active trigger (toggle handles that)
    if (this.activeTrigger && this.activeTrigger.contains(event.target)) {
      return;
    }
    // Don't close if clicking inside the active sheet
    if (this.activeSheet && this.activeSheet.contains(event.target)) {
      return;
    }
    this.close();
  }

  // Handle clicks on the container overlay (mobile)
  closeOnContainerClick(event) {
    if (!this.isOpen()) return;

    // Only close if clicking directly on container, not on a sheet
    if (this.activeSheet && this.activeSheet.contains(event.target)) {
      return;
    }
    this.close();
  }

  // Mobile behaviors
  openMobile() {
    document.body.style.overflow = "hidden";
    this.setMapInteraction(false);
    // Enable clicks on container to close sheet
    if (this.hasContainerTarget) {
      this.containerTarget.classList.add("filter-sheets-container--open");
    }
  }

  closeMobile() {
    document.body.style.overflow = "";
    this.restoreMapInteraction();
    // Disable clicks on container
    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("filter-sheets-container--open");
    }
  }

  setMapInteraction(enabled) {
    if (!this.mapElement) return;
    this.mapElement.dispatchEvent(
      new CustomEvent("map:setInteraction", { detail: { enabled } }),
    );
  }

  restoreMapInteraction() {
    if (!this.mapElement || !this.mobileSheetElement) return;

    // Only enable map if mobile sheet is in collapsed state
    const mobileSheetState =
      this.mobileSheetElement.dataset.mobileSheetStateValue;
    const enabled = mobileSheetState === "collapsed";
    this.setMapInteraction(enabled);
  }

  // State tracking
  getCheckboxState(sheet) {
    if (!sheet) return "";
    return Array.from(sheet.querySelectorAll('input[type="checkbox"]'))
      .filter((cb) => cb.checked)
      .map((cb) => cb.value)
      .sort()
      .join(",");
  }

  isDirty(sheet) {
    return (
      this.initialState !== null &&
      this.initialState !== this.getCheckboxState(sheet)
    );
  }
}
